import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  Briefcase, 
  Store,
  ShoppingBag,
  Wrench, 
  User,
  Users,
  Check,
  Loader2,
  ArrowLeft,
  X
} from 'lucide-react';
import './RoleSelection.css';

const ROLES = [
  { 
    key: 'seller', 
    icon: Store,
    color: '#F97316' // orange
  },
  { 
    key: 'buyer', 
    icon: ShoppingBag,
    color: '#3b82f6' // blue
  },
  { 
    key: 'student', 
    icon: GraduationCap,
    color: '#8B5CF6' // violet
  },
  { 
    key: 'service', 
    icon: Wrench,
    color: '#10b981' // emerald
  },
  { 
    key: 'professional', 
    icon: Briefcase,
    color: '#6366F1' // indigo
  },
  { 
    key: 'general', 
    icon: Users,
    color: '#6b7280' // gray
  }
];

function RoleSelection({ onComplete, isModal = false, currentRole = null, showBackButton = false, onBack }) {
  const { t, i18n } = useTranslation();
  // Shared unified helper — keeps user.role / account_type / profile_type in
  // sync and persists to both backend endpoints. This is the ONLY path that
  // should be used to change profile type / role app-wide.
  const { setProfileType } = useAuth();
  const [selectedRole, setSelectedRole] = useState(currentRole);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const isRTL = i18n.language?.startsWith('ar');

  const handleSelectRole = async () => {
    if (!selectedRole) return;

    setLoading(true);
    setError(null);

    try {
      const ok = await setProfileType(selectedRole);
      if (ok) {
        toast.success(t('rolesele.accountTypeUpdatedSuccessfully'));
        if (onComplete) onComplete(selectedRole);
      } else {
        setError(t('error'));
      }
    } catch (err) {
      console.error('Role update error:', err);
      setError(t('errNetwork'));
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    // Set default role to 'general' and continue
    handleSelectRoleValue('general');
  };

  const handleSelectRoleValue = async (role) => {
    setSelectedRole(role);
    setLoading(true);
    setError(null);

    try {
      const ok = await setProfileType(role);
      if (ok) {
        toast.success(t('rolesele.accountTypeUpdatedSuccessfully'));
        if (onComplete) onComplete(role);
      } else {
        setError(t('error'));
      }
    } catch (err) {
      console.error('Role update error:', err);
      setError(t('errNetwork'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (onComplete) {
      onComplete(null); // Close without saving
    }
  };

  return (
    <div className={`role-selection ${isModal ? 'role-selection--modal' : ''}`} dir={isRTL ? 'rtl' : 'ltr'} data-testid="role-selection">
      {/* Wassla Header - Green variant with centered title and X button in top-right */}
      <div className="role-selection__header-bar">
        {/* Left spacer for centering */}
        <div className="role-selection__header-spacer" />
        
        {/* Centered Title */}
        <h2 className="role-selection__header-title">
          {t('roles.selectTitle')}
        </h2>
        
        {/* Close Button - Top Right */}
        <button 
          className="role-selection__close-btn"
          onClick={handleBack}
          aria-label={t('rolesele.close')}
          data-testid="role-close-btn"
        >
          <X size={22} />
        </button>
      </div>
      
      {/* Scrollable Content Area */}
      <div className="role-selection__scroll-container">
        <div className="role-selection__content">
          <div className="role-selection__intro">
            <p className="role-selection__subtitle">{t('roles.selectSubtitle')}</p>
          </div>
          
          <div className="role-selection__grid" data-testid="role-grid">
            {ROLES.map((role) => {
              const IconComponent = role.icon;
              const isSelected = selectedRole === role.key;
              
              return (
                <button
                  key={role.key}
                  className={`role-card ${isSelected ? 'role-card--selected' : ''}`}
                  onClick={() => setSelectedRole(role.key)}
                  data-testid={`role-${role.key}`}
                  disabled={loading}
                  style={{ '--role-color': role.color }}
                >
                  <div className="role-card__icon-wrapper">
                    <IconComponent size={28} className="role-card__icon" />
                    {isSelected && (
                      <div className="role-card__check">
                        <Check size={14} />
                      </div>
                    )}
                  </div>
                  <h3 className="role-card__title">{t(`roles.${role.key}`)}</h3>
                  <p className="role-card__desc">{t(`roles.${role.key}Desc`)}</p>
                </button>
              );
            })}
          </div>
          
          {error && (
            <div className="role-selection__error" data-testid="role-error">
              {error}
            </div>
          )}
          
          {/* Footer note inside scroll area */}
          <p className="role-selection__note">{t('roles.changeNote')}</p>
        </div>
      </div>
      
      {/* Sticky Bottom Bar with Confirm Button */}
      <div className="role-selection__bottom-bar">
        <button
          className="role-selection__btn role-selection__btn--primary"
          onClick={handleSelectRole}
          disabled={!selectedRole || loading}
          data-testid="confirm-role-btn"
        >
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              {t('loading')}
            </>
          ) : (
            t('confirm')
          )}
        </button>
        
        {isModal && (
          <button
            className="role-selection__btn role-selection__btn--secondary"
            onClick={handleSkip}
            disabled={loading}
            data-testid="skip-role-btn"
          >
            {t('roles.skipForNow')}
          </button>
        )}
      </div>
    </div>
  );
}

export default RoleSelection;
